import React, { useState, useEffect } from 'react';
import { getUserOrgs } from '../../services/firestoreService';
import { createTicket } from '../../services/supportTicketService';
import { Home, Plus, Settings, DollarSign, Shield, HelpCircle, Archive, ClipboardList, MessageSquare } from 'lucide-react';

const OrgSwitcher = ({ orgId, userOrgIds, onSwitchOrg, collaboratingOrgs }) => {
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);

  const hasMultipleOrgs = (userOrgIds?.length > 1 || (collaboratingOrgs?.length > 0));

  useEffect(() => {
    const fetchOrgs = async () => {
      const idsToFetch = userOrgIds?.length ? userOrgIds : (orgId ? [orgId] : []);
      if (idsToFetch.length) {
        try {
          const orgList = await getUserOrgs(idsToFetch);
          setOrgs(orgList);
        } catch (err) {
          console.error('OrgSwitcher fetchOrgs error:', err);
        }
      }
    };
    fetchOrgs();
  }, [userOrgIds, collaboratingOrgs, orgId]);

  const currentOrg = orgs.find(o => o.id === orgId)
    || collaboratingOrgs?.find(o => o.orgId === orgId);

  const currentOrgName = currentOrg?.name || currentOrg?.orgName || '...';

  return (
    <div style={{ padding: '8px 12px', borderBottom: '1px solid #d0d4da', position: 'relative' }}>
      <button
        onClick={() => hasMultipleOrgs && setOpen(!open)}
        style={{
          width: '100%',
          padding: '8px 12px',
          backgroundColor: '#F5F5F5',
          border: '1px solid #d0d4da',
          borderRadius: '6px',
          cursor: hasMultipleOrgs ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'AvenirNext',
          fontSize: '13px',
          fontWeight: 600,
          color: '#071D39',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentOrgName}
        </span>
        {hasMultipleOrgs && (
          <span style={{ color: '#89A8B1', fontSize: '10px' }}>{open ? '▲' : '▼'}</span>
        )}
      </button>
      {open && hasMultipleOrgs && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '12px',
          right: '12px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #d0d4da',
          borderRadius: '6px',
          zIndex: 50,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {orgs.map((org) => (
            <button
              key={org.id}
              onClick={() => { onSwitchOrg(org.id); setOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                textAlign: 'left',
                cursor: 'pointer',
                backgroundColor: org.id === orgId ? '#F0F7E8' : '#FFFFFF',
                border: 'none',
                borderBottom: '1px solid #F0F0F0',
                fontFamily: 'AvenirNext',
                fontSize: '13px',
                color: '#071D39',
                fontWeight: org.id === orgId ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (org.id !== orgId) e.currentTarget.style.backgroundColor = '#F5F5F5'; }}
              onMouseLeave={(e) => { if (org.id !== orgId) e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
            >
              <div>{org.name || 'Unnamed Org'}</div>
              {org.id === orgId && (
                <div style={{ fontSize: '11px', color: '#75BB2E', marginTop: '2px' }}>✓ Current</div>
              )}
            </button>
          ))}
          {(collaboratingOrgs || []).length > 0 && (
            <div style={{ padding: '6px 12px', fontSize: '10px', fontWeight: 700, color: '#89A8B1', backgroundColor: '#F9F9F9', letterSpacing: '0.05em' }}>
              COLLABORATING ON
            </div>
          )}
          {(collaboratingOrgs || []).map((collab) => (
            <button
              key={collab.orgId + collab.dealId}
              onClick={() => { onSwitchOrg(collab.orgId); setOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                textAlign: 'left',
                cursor: 'pointer',
                backgroundColor: collab.orgId === orgId ? '#F0F7E8' : '#FFFFFF',
                border: 'none',
                borderBottom: '1px solid #F0F0F0',
                fontFamily: 'AvenirNext',
                fontSize: '13px',
                color: '#071D39',
                fontWeight: collab.orgId === orgId ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (collab.orgId !== orgId) e.currentTarget.style.backgroundColor = '#F5F5F5'; }}
              onMouseLeave={(e) => { if (collab.orgId !== orgId) e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
            >
              <div>{collab.orgName || 'Unnamed Org'}</div>
              <div style={{ fontSize: '11px', color: '#516469', marginTop: '2px' }}>
                {collab.dealTitle || 'Shared deal'} · Collaborator
              </div>
              {collab.orgId === orgId && (
                <div style={{ fontSize: '11px', color: '#75BB2E', marginTop: '2px' }}>✓ Current</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ projects, onSelectProject, onPipeline, onDashboard, onArchive, onNewProject, onTeamSettings, onMyProfile, onHelpSupport, onSuperAdmin, currentView, selectedProjectId, onLogout, getDealTitle, isAdmin, isSuperAdmin, orgId, user, userOrgIds, onSwitchOrg, impersonation, collaboratingOrgs, isCollaboratingOrg }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [currentView, isMobile]);

  return (
    <>
      {isMobile && (
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            position: 'fixed',
            // Push below iOS status bar / notch on native; no-op on web.
            top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
            left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
            zIndex: 60,
            backgroundColor: '#071D39',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '20px',
            cursor: 'pointer',
          }}
        >
          ☰
        </button>
      )}

      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 45,
          }}
        />
      )}

      <div className="w-64 border-r flex flex-col" style={{
        backgroundColor: '#FFFFFF',
        borderColor: '#d0d4da',
        ...(isMobile ? {
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 50,
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease',
        } : {}),
      }}>

        {/* Logo */}
        <div className="p-4 border-b flex items-center justify-center" style={{ backgroundColor: '#071D39', borderColor: '#071D39' }}>
          <div className="text-center w-full">
            <img
              src="/closd-logo-dark-bg.png"
              alt="CLOSD Logo"
              className="h-16 w-auto mx-auto object-contain"
            />
          </div>
        </div>

        {/* Org Switcher */}
        <OrgSwitcher orgId={orgId} userOrgIds={userOrgIds} onSwitchOrg={onSwitchOrg} collaboratingOrgs={collaboratingOrgs} />

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto" style={{ display: 'flex', flexDirection: 'column' }}>

          {/* Nav links */}
          {isAdmin && (
            <button
              onClick={onPipeline}
              className="w-full text-left p-4 flex items-center gap-2"
              style={{
                backgroundColor: currentView === 'pipeline' ? '#e8eaed' : 'transparent',
                color: currentView === 'pipeline' ? '#071D39' : '#89A8B1',
              }}
            >
              <Home className="w-5 h-5" />
              Dashboard
            </button>
          )}

          <button
            onClick={onDashboard}
            className="w-full text-left p-4 flex items-center gap-2"
            style={{
              backgroundColor: currentView === 'dashboard' ? '#e8eaed' : 'transparent',
              color: currentView === 'dashboard' ? '#071D39' : '#89A8B1',
            }}
          >
            <ClipboardList className="w-5 h-5" />
            Tasks
          </button>

          {isAdmin && (
            <button
              onClick={onTeamSettings}
              className="w-full text-left p-4 flex items-center gap-2"
              style={{
                backgroundColor: currentView === 'teamSettings' ? '#e8eaed' : 'transparent',
                color: currentView === 'teamSettings' ? '#071D39' : '#89A8B1',
              }}
            >
              <Settings className="w-5 h-5" />
              Settings
            </button>
          )}

          <button
            onClick={onHelpSupport}
            className="w-full text-left p-4 flex items-center gap-2"
            style={{
              backgroundColor: currentView === 'helpSupport' ? '#e8eaed' : 'transparent',
              color: currentView === 'helpSupport' ? '#071D39' : '#89A8B1',
            }}
          >
            <HelpCircle className="w-5 h-5" />
            Help & Support
          </button>

          <button
            onClick={() => { setShowFeedback(true); setFeedbackText(''); setFeedbackDone(false); }}
            className="w-full text-left p-4 flex items-center gap-2"
            style={{ backgroundColor: 'transparent', color: '#89A8B1' }}
          >
            <MessageSquare className="w-5 h-5" />
            Share Feedback
          </button>

          {isSuperAdmin && !impersonation && (
            <button
              onClick={onSuperAdmin}
              className="w-full text-left p-4 flex items-center gap-2"
              style={{
                backgroundColor: currentView === 'superAdmin' ? '#e8eaed' : 'transparent',
                color: currentView === 'superAdmin' ? '#071D39' : '#E53E3E',
                fontWeight: '600',
              }}
            >
              <Shield className="w-5 h-5" />
              SuperAdmin
            </button>
          )}

          {/* Active Deals */}
          <div className="p-4 text-sm font-semibold" style={{ color: '#89A8B1' }}>ACTIVE DEALS</div>

          {/* Sellers */}
          {projects.filter(p => p.type === 'Seller').length > 0 && (
            <>
              <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wide" style={{ color: '#89A8B1' }}>
                Sellers
              </div>
              {[...projects.filter(p => p.type === 'Seller')].sort((a, b) => {
                if (a.id === selectedProjectId) return -1;
                if (b.id === selectedProjectId) return 1;
                return 0;
              }).map((project) => (
                <div key={project.id} className="mx-3 mb-2">
                  <button
                    onClick={() => onSelectProject(project)}
                    className="w-full text-left px-3 py-1 rounded"
                    style={{
                      backgroundColor: selectedProjectId === project.id && currentView === 'project' ? '#071D39' : '#F5F5F5',
                      border: selectedProjectId === project.id && currentView === 'project' ? '2px solid #75BB2E' : '1px solid #d0d4da',
                      borderLeft: selectedProjectId === project.id && currentView === 'project' ? '4px solid #75BB2E' : '1px solid #d0d4da',
                    }}
                  >
                    <div className="font-semibold text-sm" style={{ color: selectedProjectId === project.id && currentView === 'project' ? '#FFFFFF' : '#071D39' }}>
                      {getDealTitle ? getDealTitle(project) : project.propertyAddress}
                    </div>
                    <div className="text-xs" style={{ color: selectedProjectId === project.id && currentView === 'project' ? '#75BB2E' : '#89A8B1' }}>
                      {project.clientName}
                    </div>
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Buyers */}
          {projects.filter(p => p.type === 'Buyer').length > 0 && (
            <>
              <div className="px-4 py-1 text-xs font-semibold uppercase tracking-wide mt-2" style={{ color: '#89A8B1' }}>
                Buyers
              </div>
              {[...projects.filter(p => p.type === 'Buyer')].sort((a, b) => {
                if (a.id === selectedProjectId) return -1;
                if (b.id === selectedProjectId) return 1;
                return 0;
              }).map((project) => (
                <div key={project.id} className="mx-3 mb-2">
                  <button
                    onClick={() => onSelectProject(project)}
                    className="w-full text-left px-3 py-1 rounded"
                    style={{
                      backgroundColor: selectedProjectId === project.id && currentView === 'project' ? '#071D39' : '#F5F5F5',
                      border: selectedProjectId === project.id && currentView === 'project' ? '2px solid #75BB2E' : '1px solid #d0d4da',
                      borderLeft: selectedProjectId === project.id && currentView === 'project' ? '4px solid #75BB2E' : '1px solid #d0d4da',
                    }}
                  >
                    <div className="font-semibold text-sm" style={{ color: selectedProjectId === project.id && currentView === 'project' ? '#FFFFFF' : '#071D39' }}>
                      {getDealTitle ? getDealTitle(project) : project.clientName}
                    </div>
                    <div className="text-xs" style={{ color: selectedProjectId === project.id && currentView === 'project' ? '#75BB2E' : '#89A8B1' }}>
                      {project.propertyAddress || 'No property yet'}
                    </div>
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Spacer to push bottom buttons down */}
          <div style={{ flex: 1 }} />

          {/* New Deal */}
          <div className="mx-3 mt-2 mb-1">
            {isAdmin ? (
              <button
                onClick={onNewProject}
                className="w-full p-3 rounded font-semibold flex items-center justify-center gap-2"
                style={{ color: '#FFFFFF', backgroundColor: '#75BB2E', border: 'none' }}
              >
                <Plus className="w-5 h-5" />
                New Deal
              </button>
            ) : (
              <div
                className="w-full p-3 rounded font-semibold flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                style={{ color: '#FFFFFF', backgroundColor: '#75BB2E' }}
                title="Only Admins can create new deals"
              >
                <Plus className="w-5 h-5" />
                New Deal
              </div>
            )}
          </div>

          {/* Deal Archive */}
          <button
            onClick={onArchive}
            className="mx-4 mb-1 p-3 rounded font-semibold flex items-center justify-center gap-2"
            style={{
              backgroundColor: currentView === 'archive' ? '#e8eaed' : 'transparent',
              color: currentView === 'archive' ? '#071D39' : '#89A8B1',
              border: '1px solid #d0d4da',
            }}
          >
            <Archive className="w-5 h-5" />
            Deal Archive
          </button>

          {/* Logout */}
          {onLogout && (
            <button
              onClick={onLogout}
              className="mx-4 mb-4 p-3 rounded font-semibold"
              style={{ borderColor: '#d0d4da', color: '#89A8B1', backgroundColor: 'transparent', border: '1px solid #d0d4da' }}
            >
              Logout
            </button>
          )}

        </div>
      </div>
    {showFeedback && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{
            backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '28px',
            width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            {feedbackDone ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>🎉</div>
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#071D39', marginBottom: '8px' }}>
                  Thanks for the feedback!
                </div>
                <p style={{ fontSize: '14px', color: '#516469', marginBottom: '20px' }}>
                  We read every submission and use it to make CLOSd better.
                </p>
                <button
                  onClick={() => setShowFeedback(false)}
                  style={{
                    backgroundColor: '#75BB2E', color: '#FFFFFF', border: 'none',
                    borderRadius: '8px', padding: '10px 28px', fontWeight: 700,
                    fontSize: '14px', cursor: 'pointer', fontFamily: 'AvenirNext',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#071D39', marginBottom: '4px' }}>
                  Share Feedback
                </div>
                <p style={{ fontSize: '13px', color: '#516469', marginBottom: '16px' }}>
                  What's working? What could be better? We'd love to know.
                </p>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Type your feedback here..."
                  rows={5}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: '1px solid #89A8B1', fontSize: '14px', color: '#071D39',
                    fontFamily: 'AvenirNext', resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '16px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowFeedback(false)}
                    style={{
                      backgroundColor: 'transparent', color: '#516469',
                      border: '1px solid #d0d4da', borderRadius: '8px',
                      padding: '9px 20px', fontWeight: 600, fontSize: '14px',
                      cursor: 'pointer', fontFamily: 'AvenirNext',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!feedbackText.trim()) return;
                      setFeedbackSubmitting(true);
                      try {
                        await createTicket({
                          orgId,
                          uid: user.uid,
                          userName: `${user.firstName} ${user.lastName || ''}`.trim(),
                          subject: 'App Feedback',
                          description: feedbackText.trim(),
                          priority: 'low',
                        });
                        setFeedbackDone(true);
                      } catch (err) {
                        console.error('Error submitting feedback:', err);
                      }
                      setFeedbackSubmitting(false);
                    }}
                    disabled={feedbackSubmitting || !feedbackText.trim()}
                    style={{
                      backgroundColor: feedbackSubmitting || !feedbackText.trim() ? '#89A8B1' : '#75BB2E',
                      color: '#FFFFFF', border: 'none', borderRadius: '8px',
                      padding: '9px 20px', fontWeight: 700, fontSize: '14px',
                      cursor: feedbackSubmitting || !feedbackText.trim() ? 'not-allowed' : 'pointer',
                      fontFamily: 'AvenirNext',
                    }}
                  >
                    {feedbackSubmitting ? 'Sending...' : 'Send Feedback'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;